import { format, m, type Message } from '@/i18n';
import { errOf, type Result } from './types';

/**
 * Account deletion, the pure half (docs/MODEL.md §6b). Deleting an account is
 * an act of the identity plane, the inverse of signing up, and it has two
 * phases:
 *
 *  1. **The tombstone** (`persons.deleted_at`): takes effect at once. Every way
 *     in closes (sessions deleted, agent keys revoked, sign-in refused) and the
 *     person leaves every picker, but nothing is erased, so an operator can
 *     restore the account during the grace period.
 *  2. **The scrub** (`persons.scrubbed_at`): after the grace period the
 *     personal data goes (name, address, credentials, private state). It
 *     cannot be undone.
 *
 * The work graph and the event log are never touched by either phase: seats,
 * grants and memberships keep naming `person:<id>`, and a scrubbed person
 * renders as {@link DELETED_ACCOUNT_LABEL} in the reader's language. What is legal and when the scrub
 * falls due are decided here; the rows live in `src/server/account-deletion.ts`.
 */

/** Days between the tombstone and the scrub. The value roll4ruin uses for its
 *  own two-phase deletion. */
export const ACCOUNT_DELETION_GRACE_DAYS = 30;

const DAY_MS = 24 * 60 * 60 * 1000;

/** The name a scrubbed account wears, formatted per reader: the directory
 *  (`withPlaceholders`, src/lib/directory.ts) and the operator's accounts
 *  list name a scrubbed person with it. */
export const DELETED_ACCOUNT_LABEL: Message = m('common.deletedAccount');

/** The display name the scrub STORES (`persons.display_name`): the label in
 *  English. Only a reader with no directory (a script, the agent tools) shows
 *  it as stored; every surface formats {@link DELETED_ACCOUNT_LABEL}. */
export const DELETED_ACCOUNT_NAME = format('en', DELETED_ACCOUNT_LABEL);

/** The domain every scrubbed account's address is under. `.invalid` is a
 *  reserved top-level domain (RFC 2606), so nothing is ever delivered there;
 *  and nobody may sign up or be invited at it (`isScrubbedAddress`). */
export const SCRUBBED_EMAIL_DOMAIN = 'tesserano.invalid';

/** The address a scrubbed account carries: unique by construction (the id is),
 *  undeliverable, and it frees the real address for a new sign-up. */
export function scrubbedEmailFor(personId: string): string {
  return `deleted-${personId}@${SCRUBBED_EMAIL_DOMAIN}`;
}

/**
 * Is this address under the scrubbed accounts' domain? The account-creation
 * door (src/lib/auth.ts) and the item invite by address
 * (src/server/work-item-invites.ts) refuse it: a person id is no secret, and
 * without the refusal someone could be invited at a scrubbed account's
 * placeholder, or sign up with it before the scrub writes it.
 */
export function isScrubbedAddress(email: string): boolean {
  return email.trim().toLowerCase().endsWith(`@${SCRUBBED_EMAIL_DOMAIN}`);
}

/** When the scrub of an account tombstoned at `deletedAt` falls due. */
export function scrubDueAt(deletedAt: Date): Date {
  return new Date(deletedAt.getTime() + ACCOUNT_DELETION_GRACE_DAYS * DAY_MS);
}

/** An account tombstoned before this instant is due for the scrub at `now`. */
export function scrubCutoff(now: Date): Date {
  return new Date(now.getTime() - ACCOUNT_DELETION_GRACE_DAYS * DAY_MS);
}

export type AccountState = 'active' | 'deleted' | 'scrubbed';

/** Where an account stands: live, tombstoned (restorable), or scrubbed. */
export function accountState(row: {
  deletedAt: Date | null;
  scrubbedAt: Date | null;
}): AccountState {
  if (row.scrubbedAt) return 'scrubbed';
  if (row.deletedAt) return 'deleted';
  return 'active';
}

/** Who asks: the person themselves (`/account`) or an operator (`/admin`). */
export type DeletionRoute = 'self' | 'operator';

/** One group the account owns through a direct, active owner membership, as
 *  the last-owner guard reads it. */
export interface OwnedGroupFacts {
  id: string;
  name: string;
  /** Other active owners that can still act: owner groups, and owner persons
   *  whose account is not deleted. */
  otherOwners: number;
  /** Active members other than this person, owners or not. */
  otherMembers: number;
}

export interface DeletionFacts {
  route: DeletionRoute;
  /** The acting person's id. */
  actorId: string;
  target: { id: string; operator: boolean; deletedAt: Date | null };
  /** Operators other than the target whose accounts are not deleted. */
  otherLiveOperators: number;
  ownedGroups: readonly OwnedGroupFacts[];
}

/**
 * May this account be deleted now, by this route? Four guards:
 *
 *  - an operator deletes their own account from `/account`, like anyone, so
 *    the operator route refuses the actor's own account;
 *  - an account already deleted is refused;
 *  - the last operator whose account is not deleted cannot be deleted by
 *    either route, or the instance would have nobody to run it;
 *  - the only owner of a group that has other members cannot be deleted
 *    until another owner exists, or the group would keep members and lose
 *    every owner who can act (the last-owner floor, docs/MODEL.md §6). The
 *    refusal names the groups.
 */
export function decideAccountDeletion(facts: DeletionFacts): Result<null> {
  const { route, actorId, target } = facts;
  if (route === 'operator' && actorId === target.id) {
    return refuse(m('accountErrors.deletion.ownFromAccountPage'));
  }
  if (target.deletedAt) return refuse(m('accountErrors.deletion.alreadyDeleted'));
  if (target.operator && facts.otherLiveOperators === 0) {
    return refuse(m('accountErrors.deletion.lastOperator'));
  }
  const orphaned = facts.ownedGroups
    .filter((g) => g.otherOwners === 0 && g.otherMembers > 0)
    .map((g) => g.name)
    .sort((a, b) => a.localeCompare(b));
  if (orphaned.length > 0) {
    const names = orphaned.join(', ');
    return refuse(
      route === 'self'
        ? m('accountErrors.deletion.onlyOwnerSelf', { names })
        : m('accountErrors.deletion.onlyOwnerOther', { names }),
    );
  }
  return { ok: true, value: null };
}

/** May this account be restored? Only while it is tombstoned and not yet
 *  scrubbed: the scrub cannot be undone. */
export function decideAccountRestore(target: {
  deletedAt: Date | null;
  scrubbedAt: Date | null;
}): Result<null> {
  if (target.scrubbedAt) return refuse(m('accountErrors.deletion.scrubbed'));
  if (!target.deletedAt) {
    return errOf('no_change', m('accountErrors.deletion.notDeleted'));
  }
  return { ok: true, value: null };
}

function refuse(message: Message): Result<never> {
  return errOf('invalid_state', message);
}
